import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, Zap, RefreshCw, Download, Copy, CheckCircle, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePlanAccess } from "../components/PlanGate";
import toast from "react-hot-toast";

const REPORT_TYPES = [
  { id: "incident_report",      label: "Incident Report",                desc: "Document workplace accidents and near-misses" },
  { id: "risk_assessment",      label: "Risk Assessment Report",         desc: "Identify and evaluate workplace hazards" },
  { id: "safety_audit",         label: "Safety Audit Report",            desc: "Comprehensive HSE audit findings and recommendations" },
  { id: "method_statement",     label: "Method Statement",               desc: "Safe work procedure for specific operations" },
  { id: "environmental_impact", label: "Environmental Impact Statement", desc: "Environmental assessment for project activities" },
  { id: "toolbox_talk",         label: "Toolbox Talk Script",            desc: "Safety briefing script for site workers" },
];

const INDUSTRIES = [
  "Oil & Gas", "Construction", "Manufacturing", "Marine/Offshore",
  "Mining", "Healthcare", "Agriculture", "Telecommunications",
];

export default function HSEReport() {
  const hasAccess = usePlanAccess("hse_reports");
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [reportType,   setReportType]   = useState("risk_assessment");
  const [industry,     setIndustry]     = useState("Construction");
  const [projectName,  setProjectName]  = useState("");
  const [location,     setLocation]     = useState("");
  const [description,  setDescription]  = useState("");
  const [companyName,  setCompanyName]  = useState("");
  const [generating,   setGenerating]   = useState(false);
  const [report,       setReport]       = useState("");
  const [copied,       setCopied]       = useState(false);


  const generateReport = async () => {
    if (!projectName.trim() || !companyName.trim()) {
      toast.error("Enter company name and project name");
      return;
    }
    setGenerating(true);
    setReport("");

    try {
      const token = localStorage.getItem("access_token");
      const API   = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

      const resp = await fetch(`${API}/ai/hse-report`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          report_type:  reportType,
          industry:     industry,
          company_name: companyName,
          project_name: projectName,
          location:     location || "Nigeria",
          description:  description,
          prepared_by:  user?.full_name || "",
        }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Generation failed");

      setReport(data.report || "");
      toast.success("HSE report generated!");
    } catch (err) {
      toast.error(err.message || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    const blob = new Blob([report], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${reportType}_${projectName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded!");
  };

  const copy = () => {
    navigator.clipboard.writeText(report);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Plan gate ────────────────────────────────────────────────────
  if (!hasAccess) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px", padding: "48px 24px", textAlign: "center" }}>
      <div style={{ width: "72px", height: "72px", borderRadius: "20px", background: "rgba(255,107,53,0.08)", border: "1px solid rgba(255,107,53,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
        <Lock size={28} color="var(--accent-orange)" />
      </div>
      <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--accent-orange)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "12px" }}>Enterprise Plan Required</div>
      <h2 style={{ fontSize: "22px", fontWeight: "800", marginBottom: "12px" }}>HSE Reports locked</h2>
      <p style={{ color: "var(--text-secondary)", fontSize: "14px", maxWidth: "360px", lineHeight: "1.7", marginBottom: "28px" }}>
        Upgrade to the <strong style={{ color: "var(--accent-orange)" }}>Enterprise plan</strong> (₦75k/month) to generate AI-powered HSE reports, risk assessments, and safety audits compliant with Nigerian regulations.
      </p>
      <button
        onClick={() => navigate("/billing")}
        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 28px", background: "linear-gradient(135deg, var(--accent-orange), #e05a20)", border: "none", borderRadius: "10px", color: "#fff", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
      >
        <Zap size={15} /> Upgrade to Enterprise →
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: "960px" }}>
      <button
        onClick={() => navigate("/compliance")}
        style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "14px", marginBottom: "24px", padding: 0 }}
      >
        <ArrowLeft size={16} /> Back to Compliance Hub
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "28px" }}>
        <div style={{ width: "44px", height: "44px", background: "rgba(255,107,53,0.1)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Shield size={22} color="var(--accent-orange)" />
        </div>
        <div>
          <h1 style={{ fontSize: "24px", marginBottom: "2px" }}>HSE Report Generator</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "13px" }}>AI-powered HSE documents compliant with Nigerian regulations (NESREA, ISO 45001)</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: "24px" }}>

        {/* Left panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          <div className="card">
            <h3 style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.8px" }}>Report Type</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {REPORT_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setReportType(type.id)}
                  style={{
                    padding: "11px 14px", borderRadius: "10px", border: "1px solid",
                    cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                    background:  reportType === type.id ? "rgba(255,107,53,0.08)" : "var(--bg-secondary)",
                    borderColor: reportType === type.id ? "var(--accent-orange)"  : "var(--border)",
                  }}
                >
                  <div style={{ fontSize: "13px", fontWeight: "600", color: reportType === type.id ? "var(--accent-orange)" : "var(--text-primary)", marginBottom: "2px" }}>{type.label}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{type.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.8px" }}>Project Details</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Company Name *</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your Company Ltd" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Project / Site Name *</label>
                <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="e.g. Bonny Pipeline Rehabilitation" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Location</label>
                <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Rivers State, Nigeria" />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Industry</label>
                <select value={industry} onChange={e => setIndustry(e.target.value)}>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "5px", display: "block" }}>Additional Context (optional)</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Specific activities, incidents, or focus areas..."
                  rows={3}
                  style={{ resize: "vertical" }}
                />
              </div>
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={generateReport}
            disabled={generating}
            style={{ width: "100%", justifyContent: "center", padding: "13px", background: generating ? "var(--text-muted)" : "var(--accent-orange)", cursor: generating ? "not-allowed" : "pointer" }}
          >
            {generating
              ? <><RefreshCw size={15} style={{ animation: "spin 1s linear infinite" }} /> Generating Report...</>
              : <><Zap size={15} /> Generate HSE Report</>
            }
          </button>
        </div>

        {/* Right panel: output */}
        <div className="card" style={{ display: "flex", flexDirection: "column", minHeight: "600px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "15px" }}>Generated Report</h3>
            {report && (
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: "12px" }} onClick={copy}>
                  {copied ? <CheckCircle size={13} color="var(--accent-green)" /> : <Copy size={13} />}
                  {" "}{copied ? "Copied!" : "Copy"}
                </button>
                <button className="btn btn-primary" style={{ padding: "6px 12px", fontSize: "12px", background: "var(--accent-orange)" }} onClick={download}>
                  <Download size={13} /> Download
                </button>
              </div>
            )}
          </div>

          {!report && !generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
              <Shield size={48} style={{ marginBottom: "16px", opacity: 0.15 }} />
              <p style={{ fontSize: "14px", textAlign: "center", maxWidth: "260px", lineHeight: "1.6" }}>
                Select a report type, fill in your project details, and click Generate
              </p>
            </div>
          )}

          {generating && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: "48px", height: "48px", border: "3px solid rgba(255,107,53,0.15)", borderTopColor: "var(--accent-orange)", borderRadius: "50%", animation: "spin 0.9s linear infinite", marginBottom: "20px" }} />
              <p style={{ color: "var(--accent-orange)", fontSize: "14px", fontWeight: "600" }}>Generating your HSE report...</p>
              <p style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "8px" }}>Applying NESREA standards and Nigerian regulatory requirements</p>
            </div>
          )}

          {report && (
            <div style={{ flex: 1, overflow: "auto" }}>
              <pre style={{ fontFamily: "DM Sans, sans-serif", fontSize: "12.5px", lineHeight: "1.9", color: "var(--text-primary)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {report}
              </pre>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
